import { supabase } from '@/integrations/supabase/client';
import type { BillingData, CreatePortalSessionResponse } from './types';

export const billingService = {
  /**
   * Get billing data for the current company
   */
  getBillingData: async (companyId: string): Promise<BillingData> => {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("company_id", companyId);

    if (error) {
      console.error('[billingService] Error fetching subscription:', error);
      throw new Error(`Failed to fetch subscription: ${error.message}`);
    }

    // Handle data integrity validation
    if (data.length > 1) {
      // CRITICAL: Multiple subscriptions found - this is a data integrity error
      console.error("Critical Data Integrity Error: Found multiple active subscriptions for companyId:", companyId);
      console.error("Subscription records:", data);
      
      // Optional: Report to Sentry if available
      if ((window as any).Sentry) {
        (window as any).Sentry.captureException(new Error(`Multiple subscriptions found for company ${companyId}`), {
          extra: {
            companyId: companyId,
            subscriptionCount: data.length,
            subscriptions: data
          }
        });
      }

      return {
        subscription: null,
        dataIntegrityError: {
          type: 'multiple_subscriptions',
          count: data.length,
          companyId
        }
      };
    }

    return {
      subscription: data.length === 1 ? data[0] : null,
      dataIntegrityError: null
    };
  },

  /**
   * Create a Stripe portal session
   */
  createPortalSession: async (): Promise<CreatePortalSessionResponse> => {
    const { data, error } = await supabase.functions.invoke("create-portal-session");

    if (error) {
      console.error('[billingService] Error creating portal session:', error);
      throw new Error(`Failed to create portal session: ${error.message || 'Unknown error'}`);
    }

    if (!data.url) {
      throw new Error('Portal session URL not received');
    }

    return data;
  },
};
